// Phase I1 helper: summarise the diff between two values of a single device
// tab section, for the activity log. Pure; no React, no localStorage. Returns
// a short one-line English description, or "" when there's no meaningful
// change (used by the caller to skip the log entirely).

const MAX_NAMED_CHANGES = 3;

const LIST_KEY_BY_SECTION = {
  interfaces: "port",
  v6interfaces: "port",
  linterfaces: "id",
  switchports: "port",
  portchannels: "id",
  vlans: "id",
  stp: "vlan",
  staticroute: "network",
  ntpservers: "ip",
  syslogservers: "ip",
  snmpcommunities: "name",
  snmphosts: "host",
  snmpv3groups: "name",
  snmpv3users: "username",
  snmpv3views: "name",
  ipv4acls: "name",
  ipv6acls: "name",
  prefixlists: "name",
  communitylists: "name",
  routemaps: "name",
  hsrp: "port",
  vrrp: "port",
  dhcp: "ip",
  dhcpexclusion: "ip",
  dhcphelper: "ip",
  ntp: null,
  syslog: null,
  snmp: null,
  multicast: null,
  basicsecurity: null
};

const LABEL_BY_SECTION = {
  interfaces: "interface",
  v6interfaces: "IPv6 interface",
  linterfaces: "loopback",
  switchports: "switchport",
  portchannels: "port-channel",
  vlans: "VLAN",
  stp: "STP",
  staticroute: "static route",
  ntpservers: "NTP server",
  syslogservers: "syslog server",
  snmpcommunities: "SNMP community",
  snmphosts: "SNMP host",
  snmpv3groups: "SNMPv3 group",
  snmpv3users: "SNMPv3 user",
  snmpv3views: "SNMPv3 view",
  ipv4acls: "IPv4 ACL",
  ipv6acls: "IPv6 ACL",
  prefixlists: "prefix-list",
  communitylists: "community-list",
  routemaps: "route-map",
  hsrp: "HSRP group",
  vrrp: "VRRP group",
  dhcp: "DHCP pool",
  dhcpexclusion: "DHCP exclusion",
  dhcphelper: "DHCP helper"
};

function pluralize(label, count) {
  if (count === 1) return label;
  if (label.endsWith("s")) return label;
  return label + "s";
}

function stableSerialize(value) {
  if (value === undefined || value === null) return "null";
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")}]`;
  if (typeof value === "object") {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableSerialize(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

function isEqual(a, b) {
  return stableSerialize(a) === stableSerialize(b);
}

function describeInitialChange(before, after) {
  const prev = (before && before[0]) || {};
  const next = (after && after[0]) || {};
  if (prev.hostname !== next.hostname && next.hostname) {
    return `renamed to ${next.hostname}`;
  }
  const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  const changed = [];
  for (const key of keys) {
    if (!isEqual(prev[key], next[key])) changed.push(key);
  }
  if (!changed.length) return "";
  if (changed.length === 1) return `changed ${changed[0]}`;
  if (changed.length <= MAX_NAMED_CHANGES) return `changed ${changed.join(", ")}`;
  return `changed ${changed.length} fields`;
}

function labelFor(sectionKey) {
  return LABEL_BY_SECTION[sectionKey] || sectionKey;
}

function rowLabel(row, keyField) {
  if (!row) return "";
  if (keyField && row[keyField] !== undefined && row[keyField] !== null && row[keyField] !== "") {
    return String(row[keyField]);
  }
  if (row.hostname) return String(row.hostname);
  if (row.name) return String(row.name);
  if (row.ip) return String(row.ip);
  return "";
}

function describeListChange(before, after, sectionKey) {
  const keyField = LIST_KEY_BY_SECTION[sectionKey];
  const prev = Array.isArray(before) ? before : [];
  const next = Array.isArray(after) ? after : [];

  if (keyField) {
    const prevByKey = new Map();
    for (const row of prev) {
      const k = rowLabel(row, keyField);
      if (k) prevByKey.set(k, row);
    }
    const nextByKey = new Map();
    for (const row of next) {
      const k = rowLabel(row, keyField);
      if (k) nextByKey.set(k, row);
    }

    const added = [];
    const removed = [];
    const changed = [];
    for (const [k, row] of nextByKey) {
      if (!prevByKey.has(k)) added.push(k);
      else if (!isEqual(prevByKey.get(k), row)) changed.push(k);
    }
    for (const k of prevByKey.keys()) {
      if (!nextByKey.has(k)) removed.push(k);
    }

    const parts = [];
    const label = labelFor(sectionKey);
    if (added.length) {
      parts.push(
        added.length <= MAX_NAMED_CHANGES
          ? `added ${pluralize(label, added.length)} ${added.join(", ")}`
          : `added ${added.length} ${pluralize(label, added.length)}`
      );
    }
    if (removed.length) {
      parts.push(
        removed.length <= MAX_NAMED_CHANGES
          ? `removed ${pluralize(label, removed.length)} ${removed.join(", ")}`
          : `removed ${removed.length} ${pluralize(label, removed.length)}`
      );
    }
    if (changed.length) {
      parts.push(
        changed.length <= MAX_NAMED_CHANGES
          ? `updated ${pluralize(label, changed.length)} ${changed.join(", ")}`
          : `updated ${changed.length} ${pluralize(label, changed.length)}`
      );
    }
    if (!parts.length) return "";
    return parts.join(" \u00b7 ");
  }

  // Unkeyed list / single-object sections: fall back to a row-count summary.
  if (prev.length !== next.length) {
    const delta = next.length - prev.length;
    const label = pluralize(labelFor(sectionKey), Math.abs(delta));
    return delta > 0 ? `added ${delta} ${label}` : `removed ${Math.abs(delta)} ${label}`;
  }
  // Same length, different content: call it "updated".
  return `updated ${labelFor(sectionKey)}`;
}

/**
 * @param {unknown} before Section value as it existed on the server / previous snapshot.
 * @param {unknown} after  Section value we're about to write.
 * @param {string} sectionKey One of the known section keys (SECTION_KEYS).
 * @returns {string} One-line English summary, or "" when nothing meaningful changed.
 */
export function summarizeSectionDiff(before, after, sectionKey) {
  if (isEqual(before, after)) return "";

  if (sectionKey === "initial") {
    return describeInitialChange(before, after);
  }

  if (Array.isArray(before) || Array.isArray(after)) {
    return describeListChange(before, after, sectionKey);
  }

  // Scalar / object non-list section: vague but honest.
  return `updated ${labelFor(sectionKey)}`;
}

// Exported for tests.
export const __test__ = { stableSerialize, isEqual };
